import { useEffect, useState, useRef } from 'react';
import { getUsers, createUser, updateUser } from '../../../context/user/UserActions';
import UserDialog from '../../partials/UserDialog';
import { formatDate } from '../../../utils/CommonLogic';

export default function Users(){
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      setError(null);
      getUsers().then(res => {
        if (res?.error) {
          setError(res.error || 'Failed to load users');
          setUsers([]);
        } else {
          setUsers(Array.isArray(res.data) ? res.data : []);
        }
      }).catch(err => {
        setError(err?.message || 'Failed to load users');
        setUsers([]);
      }).finally(() => setLoading(false));
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  // client-side filtered list
  const filtered = users.filter(u => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.phone || u.phoneNumber || '').toLowerCase().includes(q);
  });

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = page * pageSize + 1;
  const end = Math.min(total, (page + 1) * pageSize);

  useEffect(() => { setPage(0); }, [query, pageSize]);

  function goto(p){ if (p<0 || p>=totalPages) return; setPage(p); }

  function pager(){
    const pages = []; const maxButtons = 7;
    let startPage = Math.max(0, page - Math.floor(maxButtons/2));
    let endPage = startPage + maxButtons - 1;
    if (endPage > totalPages - 1){ endPage = totalPages - 1; startPage = Math.max(0, endPage - (maxButtons-1)); }
    for (let p = startPage; p <= endPage; p++) pages.push(p);
    return pages.map(p => (
      <button key={p} onClick={() => goto(p)} className={`px-3 py-1 rounded ${p===page ? 'bg-red-900 text-white' : 'bg-gray-100 text-gray-700'} mr-2`}>{p+1}</button>
    ));
  }

  const pageItems = filtered.slice(page * pageSize, (page+1) * pageSize);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold">Users</h3>
        <div className="flex items-center gap-3">
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search users" className="px-3 py-2 border rounded-md w-72" />
          <button onClick={() => { setSelectedUser(null); setDialogOpen(true); }} className="btn btn-primary">Create</button>
        </div>
      </div>

      <div className="overflow-auto bg-white rounded shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Email</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Phone</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Facility</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500">Role</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {loading && (<tr><td colSpan={7} className="px-6 py-8 text-center">Loading...</td></tr>)}
            {!loading && error && (<tr><td colSpan={7} className="px-6 py-8 text-center text-red-600">{error}</td></tr>)}
            {!loading && !error && pageItems.map(u => (
              <tr key={u.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{u.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{u.email}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{u.phone || u.phoneNumber}</td>
                <td className="px-6 py-4 text-sm text-gray-700">{u.facilityName || u.facility || ''}</td>
                <td className="px-6 py-4 text-sm text-gray-700">{(() => {
                  if (u.roles && Array.isArray(u.roles)) return u.roles.map(r => (typeof r === 'string' ? r : (r.name || r))).filter(Boolean).join(', ');
                  if (u.role) {
                    if (Array.isArray(u.role)) return u.role.map(r => (typeof r === 'string' ? r : (r.name || r))).filter(Boolean).join(', ');
                    return (typeof u.role === 'string' ? u.role : (u.role.name || u.role));
                  }
                  return u.roleName || '';
                })()}</td>
                <td className="px-6 py-4 text-right text-sm">
                  <button onClick={() => { setSelectedUser(u); setDialogOpen(true); }} className="text-sm text-red-900 hover:underline">View</button>
                </td>
              </tr>
            ))}
            {!loading && !error && users.length === 0 && (<tr><td colSpan={7} className="px-6 py-6 text-center text-sm text-gray-500">No users found</td></tr>)}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600">Showing {total===0?0:start} - {end} of {total}</div>
        <div className="flex items-center">
          <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} className="mr-4 px-2 py-1 border rounded">
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <button onClick={() => goto(0)} className="px-3 py-1 mr-2 rounded bg-gray-100">First</button>
          <button onClick={() => goto(page-1)} className="px-3 py-1 mr-2 rounded bg-gray-100">Prev</button>
          {pager()}
          <button onClick={() => goto(page+1)} className="px-3 py-1 ml-2 rounded bg-gray-100">Next</button>
          <button onClick={() => goto(totalPages-1)} className="px-3 py-1 ml-2 rounded bg-gray-100">Last</button>
        </div>
      </div>

      {dialogOpen && (
        <UserDialog
          open={dialogOpen}
          user={selectedUser}
          onClose={() => { setDialogOpen(false); setSelectedUser(null); }}
          onSave={(u) => {
            if (selectedUser) {
              // update existing
              setUsers(prev => prev.map(p => (p.id === u.id ? { ...p, ...u } : p)));
            } else {
              // prepend new user
              setUsers(prev => [u, ...prev]);
            }
            setDialogOpen(false);
            setSelectedUser(null);
          }}
        />
      )}
    </div>
  );
}
